// src/pages/FrontPageManage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardMedia,
    IconButton,
    Paper
} from '@mui/material';
import { ArrowBack, Upload, Delete, Add } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import AdminHeader from '../components/AdminHeader';
import axios from 'axios';

const FrontPageManage = () => {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [images, setImages] = useState([null, null, null, null, null]);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!authLoading && (!isAuthenticated || user?.role !== 'ADMIN')) {
            navigate('/login');
        }
    }, [isAuthenticated, user, authLoading, navigate]);

    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/front-page/images`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const newImages = [null, null, null, null, null];

            res.data.forEach((img) => {
                if (img.position >= 1 && img.position <= 5) {
                    newImages[img.position - 1] = img;
                }
            });

            setImages(newImages);
        } catch (err) {
            console.error(err);
            setError('Грешка при вчитување на слики');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (position, event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const data = new FormData();
            data.append('image', file);
            data.append('position', position);

            await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/front-page/images`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setSuccess(`Банер ${position} е успешно додаден!`);
            fetchImages();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('❌ Upload failed:', err.response?.data || err.message);
            setError(
                'Грешка при upload: ' +
                (err.response?.data?.message || err.message)
            );
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (position) => {
        if (!window.confirm('Дали сте сигурни?')) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            await axios.delete(
                `${process.env.REACT_APP_API_URL}/admin/front-page/images/${position}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setSuccess(`Банер ${position} е избришан!`);
            fetchImages();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error(err);
            setError('Грешка при бришење');
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: 'background.default' }}>
            <Container maxWidth="lg">
                <AdminHeader />

                <Box sx={{ mb: 4 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/admin/sections')}
                        sx={{ mb: 3 }}
                    >
                        Назад кон секции
                    </Button>

                    <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                        🖼️ Управување со банери
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                        Овие банери се прикажуваат во carousel на почетната страна.
                        Препорачана големина: <strong>1920x400 пиксели</strong>.
                    </Typography>
                </Box>

                {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <Paper sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                        Банери за Carousel (максимум 5)
                    </Typography>

                    {loading && !images.some((img) => img) ? (
                        <Box display="flex" justifyContent="center" py={8}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {images.map((image, index) => {
                                const position = index + 1;

                                return (
                                    <Grid item xs={12} md={6} key={position}>
                                        <Card
                                            sx={{
                                                position: 'relative',
                                                border: '2px dashed',
                                                borderColor: image ? 'primary.main' : 'grey.400',
                                                backgroundColor: image ? 'transparent' : 'grey.50',
                                                borderRadius: 2,
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {image ? (
                                                <>
                                                    <CardMedia
                                                        component="img"
                                                        height="150"
                                                        image={`${process.env.REACT_APP_API_URL}${image.imageUrl}`}
                                                        alt={`Банер ${position}`}
                                                        sx={{ objectFit: 'cover' }}
                                                    />

                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            left: 8,
                                                            bgcolor: 'primary.main',
                                                            color: 'white',
                                                            px: 2,
                                                            py: 0.5,
                                                            borderRadius: 2,
                                                            fontWeight: 'bold',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        Банер {position}
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            display: 'flex',
                                                            gap: 1
                                                        }}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            component="label"
                                                            size="small"
                                                            startIcon={<Upload />}
                                                            sx={{
                                                                bgcolor: 'rgba(0,0,0,0.7)',
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(0,0,0,0.9)'
                                                                }
                                                            }}
                                                            disabled={loading}
                                                        >
                                                            Промени
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept="image/*"
                                                                onChange={(e) =>
                                                                    handleUpload(position, e)
                                                                }
                                                            />
                                                        </Button>

                                                        <IconButton
                                                            sx={{
                                                                bgcolor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    bgcolor: 'error.dark'
                                                                }
                                                            }}
                                                            onClick={() => handleDelete(position)}
                                                            disabled={loading}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Box>
                                                </>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        height: 150,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 2
                                                    }}
                                                >
                                                    <Typography variant="h6" color="text.secondary">
                                                        Банер {position}
                                                    </Typography>

                                                    <Typography variant="caption" color="text.secondary">
                                                        Препорачано: 1920x400 px
                                                    </Typography>

                                                    <Button
                                                        variant="contained"
                                                        component="label"
                                                        startIcon={<Add />}
                                                        disabled={loading}
                                                    >
                                                        Додај Банер
                                                        <input
                                                            type="file"
                                                            hidden
                                                            accept="image/*"
                                                            onChange={(e) =>
                                                                handleUpload(position, e)
                                                            }
                                                        />
                                                    </Button>
                                                </Box>
                                            )}
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    <Alert severity="info" sx={{ mt: 4 }}>
                        <Typography variant="body2">
                            💡 <strong>Совет:</strong> Користете слики 1920x400 пиксели за најдобар изглед.
                        </Typography>
                    </Alert>
                </Paper>
            </Container>
        </Box>
    );
};

export default FrontPageManage;